import { TEnemy } from '../enemy/enemy.generators';
import { TLocation } from '../locations/location.generators';
import { ImmerStateCreator } from './store.types';

export const GameLogItemType = {
   EnemyAttacksSigg: 'EnemyAttacksSigg',
   SiggAttacksEnemy: 'SiggAttacksEnemy',
   SiggUsesPotion: 'SiggUsesPotion',
} as const;
export type GameLogItemType = (typeof GameLogItemType)[keyof typeof GameLogItemType];

type TGameLogBaseItem = {
   type: GameLogItemType;
   timestamp: Date;
   location: TLocation;
};

type TEnemyAttacksSiggItem = TGameLogBaseItem & {
   attackAmount: number;
   enemy: TEnemy;
};

type TSiggAttacksEnemyItem = TGameLogBaseItem & {
   attackAmount: number;
   enemy: TEnemy;
};

type TSiggUsesPotionOnEnemyItem = TGameLogBaseItem & {
   potionName: string;
   enemy: TEnemy;
};

type TGameLogItem = TEnemyAttacksSiggItem | TSiggAttacksEnemyItem | TSiggUsesPotionOnEnemyItem;

type TGameLogState = {
   state: {
      logs: TGameLogItem[];
   };
};

type TGameLogActions = {
   actions: {
      addLogItem(item: TGameLogItem): void;
      addLogItemEnemyAttacksSigg(item: Omit<TEnemyAttacksSiggItem, 'timestamp' | 'type'>): void;
      addLogItemSiggAttacksEnemy(item: Omit<TSiggAttacksEnemyItem, 'timestamp' | 'type'>): void;
      addLogItemSiggUsesPotion(item: Omit<TSiggUsesPotionOnEnemyItem, 'timestamp' | 'type'>): void;
      resetLog(): void;
   };
};

type TGameLogGetters = {
   getters: {
      getLogItemsForLocation(params: { locationName: string }): TGameLogItem[];
   };
};

type TGameLogSlice = TGameLogState & TGameLogActions & TGameLogGetters;
export type TNamedGameLogSlice = { log: TGameLogSlice };

export const getGameLogInitialState = (): TGameLogState['state'] => {
   return {
      logs: [],
   };
};

export const createGameLogSlice: ImmerStateCreator<TNamedGameLogSlice, TGameLogSlice> = (set, get, store) => ({
   /***************************
    * State
    ***************************/
   state: getGameLogInitialState(),

   /***************************
    * Actions
    ***************************/
   actions: {
      addLogItem(item: TGameLogItem) {
         return set((state) => {
            state.log.state.logs.push({
               ...item,
               timestamp: new Date(),
            } as TGameLogItem);
         });
      },

      addLogItemEnemyAttacksSigg(item: Omit<TEnemyAttacksSiggItem, 'timestamp' | 'type'>) {
         return get().log.actions.addLogItem({
            ...item,
            type: GameLogItemType.EnemyAttacksSigg,
         } as TGameLogItem);
      },

      addLogItemSiggAttacksEnemy(item: Omit<TSiggAttacksEnemyItem, 'timestamp' | 'type'>) {
         return get().log.actions.addLogItem({
            ...item,
            type: GameLogItemType.SiggAttacksEnemy,
         } as TGameLogItem);
      },

      addLogItemSiggUsesPotion(item: Omit<TSiggUsesPotionOnEnemyItem, 'timestamp' | 'type'>) {
         return get().log.actions.addLogItem({
            ...item,
            type: GameLogItemType.SiggUsesPotion,
         } as TGameLogItem);
      },

      resetLog() {
         return set((state) => {
            state.log.state.logs = [];
         });
      },
   },

   /***************************
    * Getters
    ***************************/
   getters: {
      getLogItemsForLocation(params: { locationName: string }) {
         return get().log.state.logs.filter((logItem) => logItem.location.name === params.locationName);
      },
   },
});
